import React, { useEffect } from 'react';
import { PrimaryButton, GhostButton } from './ModalButton';
import { colors } from '../utils/colors';

// Modal básico reutilizável
export function EmailModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  onConfirm, 
  onCancel,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  confirmDisabled = false,
  showActions = true,
  maxWidth = "md"
}) {
  // Fechar modal com tecla ESC
  useEffect(() => {
    if (!isOpen) return;
    
    function handleEscape(e) {
      if (e.key === 'Escape') {
        onClose?.();
      }
    }
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevenir scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const maxWidthValues = {
    sm: '384px',
    md: '448px',
    lg: '512px',
    xl: '576px',
    '2xl': '672px'
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: maxWidthValues[maxWidth],
          backgroundColor: colors.bg,
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
          transform: 'scale(1)',
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div style={{
            padding: '24px 24px 16px 24px',
            borderBottom: '1px solid #454535ff',
            textAlign: 'center'
          }}>
            <h2 
              style={{ 
                margin: 0,
                fontSize: '18px',
                fontWeight: 'bold',
                color: colors.text
              }}
            >
              {title}
            </h2>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {children}
        </div>

        {/* Actions */}
        {showActions && (
          <div style={{
            padding: '16px 24px 24px 24px',
            borderTop: '1px solid #454535ff',
            display: 'flex',
            justifyContent: 'center',
            gap: '12px'
          }}>
            {onCancel && (
              <GhostButton onClick={onCancel}>
                {cancelText}
              </GhostButton>
            )}
            {onConfirm && (
              <PrimaryButton 
                onClick={onConfirm}
                disabled={confirmDisabled}
              >
                {confirmText}
              </PrimaryButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Modal de confirmação de email
export function EmailConfirmationModal({ 
  isOpen, 
  email, 
  onConfirm, 
  onCancel, 
  countdown 
}) {
  return (
    <EmailModal
      isOpen={isOpen}
      onClose={onCancel}
      title="Confirme seu e-mail"
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmText={countdown > 0 ? `Continuar (${countdown}s)` : "Continuar"}
      cancelText="Voltar e corrigir"
      confirmDisabled={countdown > 0}
      maxWidth="md"
    >
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#454535ff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <svg 
              style={{
                width: '32px',
                height: '32px',
                color: colors.text
              }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
              />
            </svg>
          </div>
          
          <h3 
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '8px',
              margin: '0 0 8px 0',
              color: colors.text
            }}
          >
            Confirme seu endereço de e-mail
          </h3>
          
          <p 
            style={{
              fontSize: '14px',
              marginBottom: '16px',
              margin: '0 0 16px 0',
              color: colors.text,
              opacity: 0.8
            }}
          >
            Você está usando o e-mail:
          </p>
          
          <div 
            style={{
              backgroundColor: colors.text,
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              borderLeft: `4px solid ${colors.primary || '#2563eb'}`
            }}
          >
            <p 
              style={{
                fontWeight: '500',
                fontSize: '16px',
                margin: 0,
                color: 'black'
              }}
            >
              {email}
            </p>
          </div>
          
          <p 
            style={{
              fontSize: '14px',
              margin: 0,
              color: colors.text,
              opacity: 0.7,
              lineHeight: '1.4'
            }}
          >
            Este será o e-mail usado para acessar sua conta e receber comunicações importantes. 
            Certifique-se de que está correto antes de continuar.
          </p>
        </div>
      </div>
    </EmailModal>
  );
}
